import { applyTheme, type ThemePrefs } from "@qn/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { api, type UserSettings } from "./api";
import { usePrefs } from "./prefs";

export function useSettingsQuery(enabled: boolean) {
  return useQuery({
    queryKey: ["settings"],
    queryFn: () => api<{ settings: UserSettings }>("/settings"),
    enabled,
  });
}

/** Applies theme to <html> whenever prefs change (and follows the OS in "system" mode). */
export function useApplyTheme() {
  const {
    theme,
    style,
    accent,
    density,
    fontScale,
    chatWidth,
    readingFont,
    reduceMotion,
    ambient,
    lockZoom,
  } = usePrefs();
  // Zoom lock: no pinch / double-tap zoom (iOS ignores user-scalable=no, so the gesture events are cancelled too).
  useEffect(() => {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
    const base = "width=device-width, initial-scale=1, viewport-fit=cover";
    if (meta) meta.content = lockZoom ? `${base}, maximum-scale=1, user-scalable=no` : base;
    document.documentElement.style.touchAction = lockZoom ? "manipulation" : "";
    if (!lockZoom) return;
    const stop = (e: Event) => e.preventDefault();
    const multi = (e: TouchEvent) => {
      if (e.touches.length > 1) e.preventDefault();
    };
    document.addEventListener("gesturestart", stop);
    document.addEventListener("gesturechange", stop);
    document.addEventListener("gestureend", stop);
    document.addEventListener("touchmove", multi, { passive: false });
    return () => {
      document.removeEventListener("gesturestart", stop);
      document.removeEventListener("gesturechange", stop);
      document.removeEventListener("gestureend", stop);
      document.removeEventListener("touchmove", multi);
    };
  }, [lockZoom]);
  useEffect(() => {
    const r = document.documentElement;
    r.style.setProperty(
      "--chat-width",
      { narrow: "40rem", normal: "48rem", wide: "64rem" }[chatWidth],
    );
    r.style.setProperty(
      "--font-reading",
      {
        sans: "var(--font-sans)",
        serif: "ui-serif, Georgia, 'Times New Roman', serif",
        mono: "var(--font-mono)",
      }[readingFont],
    );
    r.dataset.motion = reduceMotion ? "reduced" : "full";
    r.dataset.ambient = ambient && !reduceMotion ? "on" : "off";
  }, [chatWidth, readingFont, reduceMotion, ambient]);
  useEffect(() => {
    const prefs: ThemePrefs = { theme, style, accent, density, fontScale };
    applyTheme(prefs);
    if (theme !== "system") return;
    const mq = matchMedia("(prefers-color-scheme: dark)");
    const on = () => applyTheme(prefs);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [theme, style, accent, density, fontScale]);
}

/** Preferences that follow the account across devices (and that the owner can set defaults for). */
const SYNCED = [
  "theme",
  "style",
  "accent",
  "locale",
  "density",
  "fontScale",
  "sendOnEnter",
  "chatWidth",
  "readingFont",
  "reduceMotion",
  "lockZoom",
  "showUsage",
] as const;
const pickSynced = (o: Record<string, unknown>) =>
  Object.fromEntries(SYNCED.filter((k) => o[k] !== undefined).map((k) => [k, o[k]]));

/** Server settings win on first load so preferences follow the user across devices. */
export function useHydratePrefs(settings: UserSettings | undefined) {
  const done = useRef(false);
  useEffect(() => {
    if (!settings || done.current) return;
    done.current = true;
    const patch = pickSynced(settings as Record<string, unknown>);
    if (Object.keys(patch).length) usePrefs.getState().set(patch);
  }, [settings]);
}

/** Update a setting locally (instant) and persist it to the account. */
export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UserSettings) => {
      const local = pickSynced(patch as Record<string, unknown>);
      if (Object.keys(local).length) usePrefs.getState().set(local);
      return api<{ settings: UserSettings }>("/settings", { method: "PUT", body: patch });
    },
    onSuccess: (r) => qc.setQueryData(["settings"], r),
  });
}
